/**
 * Role fixtures for the portal UI (docs §12). DEMO: the browser applies these to hide/disable UI and to
 * scope queries; real authorisation must be enforced by the backend (not built yet → BLOCKED, P10).
 */
export type Role = 'PLATFORM_ADMIN' | 'OPERATIONS' | 'RISK' | 'DEVELOPER' | 'FINANCE' | 'SUPPORT' | 'AUDITOR' | 'PARTNER_ADMIN';

export type Permission =
  | 'view.overview'
  | 'view.cameras'
  | 'view.rounds'
  | 'view.players'
  | 'view.bets'
  | 'view.operators'
  | 'view.risk'
  | 'view.promotions'
  | 'view.finance'
  | 'view.reconciliation'
  | 'view.audit'
  | 'view.system'
  | 'export.csv'
  | 'action.pause'
  | 'request.review'
  | 'approve.review'
  | 'request.config'
  | 'approve.config'
  | 'request.keyRotation'
  | 'approve.keyRotation'
  | 'request.adjustment'
  | 'request.connection'
  | 'approve.adjustment'
  /** Developer portal (P13): docs, keys, webhooks, conformance. Partners act on their own tenant only. */
  | 'view.developer'
  | 'dev.keys'
  | 'dev.webhooks'
  | 'dev.conformance'
  /** Platform onboarding: operators, brands, entitlements, callback hosts, certification records. */
  | 'onboarding.manage'
  /** Camera pool actions: force health check, activate/deactivate, standby, disable (audited). */
  | 'camera.manage'
  /** Portal accounts: create, delete (disable), reset password + authenticator. Admin only (audited). */
  | 'manage.users';

export interface PortalUser {
  userId: string;
  displayName: string;
  role: Role;
  /** Partner users are bound to one operator (tenant from authentication, never from a URL). */
  operatorId: string | null;
  mfa: 'DEMO_NOT_ENFORCED';
}

const VIEW_ALL: Permission[] = [
  'view.overview',
  'view.cameras',
  'view.rounds',
  'view.players',
  'view.bets',
  'view.operators',
  'view.risk',
  'view.promotions',
  'view.finance',
  'view.reconciliation',
  'view.audit',
  'view.system',
];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  PLATFORM_ADMIN: [...VIEW_ALL, 'export.csv', 'action.pause', 'request.review', 'approve.review', 'request.config', 'approve.config', 'request.keyRotation', 'approve.keyRotation', 'request.adjustment', 'approve.adjustment', 'request.connection', 'view.developer', 'dev.keys', 'dev.webhooks', 'dev.conformance', 'onboarding.manage', 'camera.manage', 'manage.users'],
  OPERATIONS: ['view.overview', 'view.cameras', 'view.rounds', 'view.bets', 'view.risk', 'view.promotions', 'view.system', 'view.audit', 'action.pause', 'request.review', 'approve.review', 'request.config', 'view.developer', 'dev.webhooks', 'camera.manage'],
  /** Trading risk: liability, prices, limits and the rounds/bets behind them; can pause a game and request config changes. */
  RISK: ['view.overview', 'view.cameras', 'view.rounds', 'view.bets', 'view.players', 'view.risk', 'view.promotions', 'view.finance', 'view.reconciliation', 'view.audit', 'export.csv', 'action.pause', 'request.review', 'request.config', 'request.adjustment'],
  /** Engineering: system, camera pipeline, developer portal (keys, webhooks, conformance); no money approvals. */
  DEVELOPER: ['view.overview', 'view.cameras', 'view.rounds', 'view.system', 'view.audit', 'view.operators', 'view.developer', 'dev.keys', 'dev.webhooks', 'dev.conformance', 'camera.manage'],
  FINANCE: ['view.overview', 'view.bets', 'view.players', 'view.rounds', 'view.finance', 'view.reconciliation', 'view.promotions', 'view.operators', 'export.csv', 'approve.config', 'request.adjustment', 'approve.adjustment'],
  SUPPORT: ['view.overview', 'view.players', 'view.bets', 'view.rounds'],
  AUDITOR: [...VIEW_ALL, 'export.csv', 'view.developer'],
  PARTNER_ADMIN: ['view.overview', 'view.rounds', 'view.players', 'view.bets', 'view.operators', 'view.finance', 'view.reconciliation', 'view.promotions', 'export.csv', 'request.keyRotation', 'request.connection', 'view.developer', 'dev.keys', 'dev.webhooks', 'dev.conformance'],
};

export const can = (u: PortalUser, p: Permission) => (ROLE_PERMISSIONS[u.role] ?? []).includes(p);

/** What each role is for (shown when an admin creates an account). */
export const ROLE_INFO: Record<Role, { label: string; summary: string }> = {
  PLATFORM_ADMIN: { label: 'Admin (full access)', summary: 'Everything, including user accounts, approvals and onboarding.' },
  OPERATIONS: { label: 'Operations', summary: 'Live game, cameras, rounds and reviews; can pause a game.' },
  RISK: { label: 'Risk', summary: 'Liability, pricing, limits, bets and finance views; can pause and request config changes.' },
  DEVELOPER: { label: 'Developer', summary: 'System, camera pipeline and developer portal (keys, webhooks, conformance).' },
  FINANCE: { label: 'Finance', summary: 'Finance, reconciliation and adjustments; approves config changes.' },
  SUPPORT: { label: 'Support', summary: 'Players, bets and rounds (read only).' },
  AUDITOR: { label: 'Auditor', summary: 'Read-only access to everything, including the audit chain.' },
  PARTNER_ADMIN: { label: 'Partner admin', summary: "One operator's own data and its developer portal only." },
};

/** Demo sign-in fixtures (no passwords; MFA/SSO arrive with the backend). */
export const DEMO_USERS: readonly PortalUser[] = [
  { userId: 'u-admin', displayName: 'Platform admin (demo)', role: 'PLATFORM_ADMIN', operatorId: null, mfa: 'DEMO_NOT_ENFORCED' },
  { userId: 'u-ops-1', displayName: 'Operations 1 (demo)', role: 'OPERATIONS', operatorId: null, mfa: 'DEMO_NOT_ENFORCED' },
  { userId: 'u-ops-2', displayName: 'Operations 2 (demo)', role: 'OPERATIONS', operatorId: null, mfa: 'DEMO_NOT_ENFORCED' },
  { userId: 'u-fin', displayName: 'Finance (demo)', role: 'FINANCE', operatorId: null, mfa: 'DEMO_NOT_ENFORCED' },
  { userId: 'u-support', displayName: 'Support (demo)', role: 'SUPPORT', operatorId: null, mfa: 'DEMO_NOT_ENFORCED' },
  { userId: 'u-audit', displayName: 'Auditor (demo)', role: 'AUDITOR', operatorId: null, mfa: 'DEMO_NOT_ENFORCED' },
  { userId: 'u-partner-alpha', displayName: 'Partner admin · Alpha Gaming (demo)', role: 'PARTNER_ADMIN', operatorId: 'op-alpha', mfa: 'DEMO_NOT_ENFORCED' },
  { userId: 'u-partner-beta', displayName: 'Partner admin · Beta Play (demo)', role: 'PARTNER_ADMIN', operatorId: 'op-beta', mfa: 'DEMO_NOT_ENFORCED' },
];
